/* eslint-disable no-unused-vars */
import React, { useState, useContext } from 'react';
import { Row, Input, FormGroup } from "reactstrap";
import CustomInfo from '../../../components/CustomInfo.jsx';
import { getCLPaymentsStatistics, getCOPaymentsStatistics, getMXPaymentsStatistics } from '../../../../../api';
import { useAuthToken } from '../../../../../hooks/AuthHook.jsx';
import { AdminStateContext } from '../../../../../providers/AdminStateProvider.jsx';

const COUNTRY_CONFIG = {
    CL: { locale: 'es-CL', currency: 'CLP' },
    CO: { locale: 'es-CO', currency: 'COP' },
    MX: { locale: 'es-MX', currency: 'MXN' },
};

const API_BY_COUNTRY = {
    CL: getCLPaymentsStatistics,
    CO: getCOPaymentsStatistics,
    MX: getMXPaymentsStatistics,
};

const MOVEMENT_TYPES = [
    { value: 'INCOME', label: 'Entrada' },
    { value: 'OUTFLOW', label: 'Salida' },
];

const MOVEMENT_CONCEPTS = [
    { value: 'LEASE', label: 'Mensualidad' },
    { value: 'CONTRACT', label: 'Contrato' },
    { value: 'RESERVATION', label: 'Reserva' },
    { value: 'REFUND', label: 'Reembolso' },
];

const MOVEMENT_STATUS = [
    { value: 'PENDING', label: 'No confirmado' },
    { value: 'UNPAID', label: 'Pendiente de pago' },
    { value: 'PAID', label: 'Pagado' },
    { value: 'REFUNDED', label: 'Reembolsado' },
    { value: 'CANCELED', label: 'Anulado' },
];

const GetPaymentsPerDate = () => {
    const authToken = useAuthToken();
    const [adminState] = useContext(AdminStateContext);
    const { country } = adminState;
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [showData, setShowData] = useState(false);
    const [selectedType, setSelectedType] = useState('');
    const [selectedConcept, setSelectedConcept] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');

    const [payments, setPayments] = useState([
        {
            basic_number: 0,
            basic_amount: 0,
            guaranteed_number: 0,
            guaranteed_amount: 0,
            total_number: 0,
            total_amount: 0,
        }
    ]);

    const handleSearch = async () => {
        if (!startDate || !endDate) return;
        if (!authToken) return;

        try {
            setLoading(true);
            setShowData(false);

            const getStatistics = API_BY_COUNTRY[country] || getCLPaymentsStatistics;
            const data = await getStatistics(authToken, {
                _from: startDate,
                _to: endDate,
                movement_type: selectedType ? [selectedType] : [],
                movement_concept: selectedConcept ? [selectedConcept] : [],
                movement_status: selectedStatus ? [selectedStatus] : [],
            });

            // Ajusta este mapeo según el contrato real del backend si cambia
            setPayments([{
                basic_number: data.basic_number ?? 0,
                basic_amount: data.basic_amount ?? 0,
                guaranteed_number: data.guaranteed_number ?? 0,
                guaranteed_amount: data.guaranteed_amount ?? 0,
                total_number: data.total_number ?? 0,
                total_amount: data.total_amount ?? 0,
            }]);

            setShowData(true);
        } catch (e) {
            console.error('Error inesperado al obtener pagos', e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <CustomInfo
            renderHeader={() => (
                <>
                    Obtencion de pagos por fecha

                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
                        {/* Selector de fecha inicio y fecha fin */}
                        <label style={{ marginRight: 8 }}>
                            Fecha inicio:
                            <Input
                                type="date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                style={{ marginLeft: 4, marginRight: 16 }}
                            />
                        </label>
                        <label>
                            Fecha fin:
                            <Input
                                type="date"
                                value={endDate}
                                min={startDate}
                                onChange={e => setEndDate(e.target.value)}
                                style={{ marginLeft: 4 }}
                            />
                        </label>
                        <button
                            style={{
                                marginLeft: 16,
                                padding: '8px 20px',
                                background: '#5e72e4',
                                color: 'white',
                                border: 'none',
                                borderRadius: 6,
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontSize: 16
                            }}
                            onClick={handleSearch}
                            disabled={!startDate || !endDate}
                        >
                            Buscar
                        </button>
                    </div>

                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <FormGroup className="my-0 mx-3">
                            <label><small>Tipo de movimiento</small></label>
                            <Input
                                className="col form-control-muted form-control-sm mx-2"
                                type="select"
                                value={selectedType}
                                onChange={e => setSelectedType(e.target.value)}
                                style={{ minWidth: 180 }}
                            >
                                <option value="">-- Todos --</option>
                                {MOVEMENT_TYPES.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup className="my-0 mx-3">
                            <label><small>Concepto</small></label>
                            <Input
                                className="col form-control-muted form-control-sm mx-2"
                                type="select"
                                value={selectedConcept}
                                onChange={e => setSelectedConcept(e.target.value)}
                                style={{ minWidth: 180 }}
                            >
                                <option value="">-- Todos --</option>
                                {MOVEMENT_CONCEPTS.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup className="my-0 mx-3">
                            <label><small>Estado</small></label>
                            <Input
                                className="col form-control-muted form-control-sm mx-2"
                                type="select"
                                value={selectedStatus}
                                onChange={e => setSelectedStatus(e.target.value)}
                                style={{ minWidth: 180 }}
                            >
                                <option value="">-- Todos --</option>
                                {MOVEMENT_STATUS.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </div>
                </>
            )}
            renderInfo={() => {
                const basicNumber = payments[0].basic_number || 0;
                const basicAmount = payments[0].basic_amount || 0;
                const guaranteedNumber = payments[0].guaranteed_number || 0;
                const guaranteedAmount = payments[0].guaranteed_amount || 0;
                const totalNumber = payments[0].total_number || 0;
                const totalAmount = payments[0].total_amount || 0;

                return (
                    <Row className="text-center">
                        <div className="col">
                            <h5>Pago Básico</h5>
                            <p>Monto: {basicAmount.toLocaleString(COUNTRY_CONFIG[country]?.locale || 'es-CL', { style: 'currency', currency: COUNTRY_CONFIG[country]?.currency || 'CLP' })}</p>
                            <p>N° pagos: <b>{basicNumber}</b></p>
                        </div>
                        <div className="col">
                            <h5>Pago Garantizado</h5>
                            <p>Monto: {guaranteedAmount.toLocaleString(COUNTRY_CONFIG[country]?.locale || 'es-CL', { style: 'currency', currency: COUNTRY_CONFIG[country]?.currency || 'CLP' })}</p>
                            <p>N° pagos: <b>{guaranteedNumber}</b></p>
                        </div>
                        <div className="col">
                            <h5>Pago Total</h5>
                            <p>Monto: {totalAmount.toLocaleString(COUNTRY_CONFIG[country]?.locale || 'es-CL', { style: 'currency', currency: COUNTRY_CONFIG[country]?.currency || 'CLP' })}</p>
                            <p>N° pagos: <b>{totalNumber}</b></p>
                        </div>
                    </Row>
                );
            }}
            loading={loading}
            showData={showData}
        />
    );
}

export default GetPaymentsPerDate;
